import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import menuMeatballTokens from '@amzn/meridian-tokens/base/icon/menu-meatball';
import syncTokens from '@amzn/meridian-tokens/base/icon/sync';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useRefreshSyncTargetMutation } from 'src/store/bender-api';
import { type SyncTargetDto, SyncTargetStatuses } from 'src/types/sync-target.types';
import { boxFolderUrl } from 'src/utils/syncTarget';
import { normalizeBenderError } from 'src/utils/benderErrors';
import { Logger } from 'src/utils/logger';

export interface BoxFolderActionsMenuProps {
  syncTarget: SyncTargetDto;
  /** Prefix for data-testids, e.g. `box-folder-409789436486`. */
  testIdPrefix: string;
  onEditExclusions: () => void;
}

/**
 * Per-folder actions: edit exclusions, request a sync, or open the folder in Box.
 *
 * **Pause, Resume, and Remove are all deliberately absent.** Enrollment is global —
 * every folder here feeds every campaign using a template that pulls from Box — and Bender
 * has no user-identity layer, so it cannot tell one Leela user from another. Any of the
 * three would let an arbitrary user stop or delete a folder somebody else enrolled,
 * affecting every Box campaign at once with no attributable trail.
 *
 * Resume is excluded for a sharper reason than the other two: with Pause gone, a folder
 * can only reach `PAUSED` from outside Leela, so Resume's only possible function was
 * reverting a deliberate admin decision. Leela pins a fixed daily cadence at enrollment
 * and deliberately exposes no schedule edit UI; schedule editing remains in Bender's
 * own admin UI.
 */
export const BoxFolderActionsMenu: React.FC<BoxFolderActionsMenuProps> = ({
  syncTarget,
  testIdPrefix,
  onEditExclusions,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Untyped per the repo's row-actions convention.
  const menuRef = useRef();

  const [refreshSyncTarget, { isLoading: isRefreshing }] = useRefreshSyncTargetMutation();

  const { syncTargetId, targetId, status } = syncTarget;

  // Stricter than the server on purpose: Bender accepts a refresh on QUEUED and
  // IN_PROGRESS but answers 202 without firing anything, so the action would visibly do
  // nothing. PAUSED is refused outright with a 409. Do not loosen this to match.
  const canSyncNow = status === SyncTargetStatuses.ACTIVE || status === SyncTargetStatuses.ERROR;

  const handleSyncNow = async () => {
    try {
      await refreshSyncTarget({ syncTargetId }).unwrap();
      // Bender answers 202 — the sync is queued, not performed. The copy says
      // "Sync requested" rather than "Synced" for that reason.
      createToast({ type: AlertType.SUCCESS, message: t('box_folder_sync_requested') });
    } catch (error) {
      const normalized = normalizeBenderError(error);
      Logger.error(`Box folder sync request failed for ${syncTargetId}`, {
        status: normalized.status,
        detail: normalized.message,
      });

      // A 409 means the folder was paused behind our back; the mutation still
      // invalidates the cache tags, so the refetch corrects the row and the action
      // disappears rather than inviting an identical second click. An undefined status
      // means no HTTP response at all (network or CORS).
      const messageKey = normalized.status === 409
        ? 'box_folder_sync_conflict'
        : normalized.status === undefined
          ? 'box_folder_error_unavailable'
          : 'box_folder_sync_error';

      createToast({ type: AlertType.ERROR, message: t(messageKey) });
    }
  };

  /** `aria-disabled` on MenuItem is presentational only, so each handler re-checks. */
  const runAction = (enabled: boolean, action: () => void) => () => {
    if (!enabled || isRefreshing) return;
    setIsMenuOpen(false);
    action();
  };

  return (
    <>
      <Button
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-label={t('table_header_actions')}
        data-testid={`${testIdPrefix}-actions-menu-button`}
        propagateClickEvent={false}
        ref={menuRef}
        type="icon"
        onClick={() => { setIsMenuOpen(true); }}
      >
        <Icon tokens={menuMeatballTokens} />
      </Button>
      <Menu
        anchorNode={menuRef.current}
        minWidth={200}
        open={isMenuOpen}
        position="bottom"
        onClose={() => { setIsMenuOpen(false); }}
      >
        {canSyncNow && (
          <MenuItem
            aria-disabled={isRefreshing}
            data-testid={`${testIdPrefix}-sync-now-button`}
            iconTokens={syncTokens}
            onClick={runAction(canSyncNow, () => { void handleSyncNow(); })}
          >
            {t('box_folder_sync_now')}
          </MenuItem>
        )}
        <MenuItem
          aria-disabled={isRefreshing}
          data-testid={`${testIdPrefix}-edit-exclusions-button`}
          iconTokens={editTokens}
          onClick={runAction(true, onEditExclusions)}
        >
          {t('box_folder_edit_exclusions')}
        </MenuItem>
        <MenuItem
          data-testid={`${testIdPrefix}-open-in-box-button`}
          iconTokens={exportSmallTokens}
          onClick={runAction(true, () => {
            window.open(boxFolderUrl(targetId), '_blank', 'noopener,noreferrer');
          })}
        >
          {t('box_folder_open_in_box')}
        </MenuItem>
      </Menu>
    </>
  );
};
